import { useState, useMemo } from "react";
import { usePeriodicLoadData } from "../hooks/usePeriodicLoadData";
import Button from "./common/Button";
import { Card, CardHeader, CardTitle, CardContent } from "./common/Card";
import { Badge } from "./common/Badge";
import { Calendar } from "./common/Calendar";
import { Label } from "./common/Label";
import Switch from "./common/Switch";
import { Textarea } from "./common/Textarea";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "./common/Dialog";
import * as utils from "../utils/utils";
import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import PatientSummaryInfoDialog from "./PatientSummaryInfoDialog";
import customToast from "./common/Toaster";
import { cancelBulkByDates } from "../apis/api.appointments";

import { getLocalizedDayName } from "../i18n/scheduleLabels";
import {
  generateTimeSlots,
  formatDate,
  formatDateDMY,
  addDays,
  isOccupiedAppointment,
  formatClockTime,
  formatClockTimeRange,
  getActiveSchedulesForDate,
  getScheduleConsultationMode,
} from "../lib/ScheduleUtils";
import { ConsultationModeBadge } from "./ConsultationMode";

import {
  CalendarDays,
  Clock,
  User,
  NotepadText,
  Phone,
  ArrowLeft,
  Users,
  CheckCircle,
  CalendarX,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import useBrandTranslation from "../hooks/useBrandTranslation";

export default function DoctorAppointmentsView() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const {
    getSchedulesByDoctor,
    getAppointmentsByDoctor,
    secretaries,
    getProfileFromCache,
    loadData,
  } = useData();

  usePeriodicLoadData(loadData, undefined, user?.role === "doctor");
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [cancelModeEnabled, setCancelModeEnabled] = useState(false);
  const [datesToCancel, setDatesToCancel] = useState([]);
  const [cancellationReason, setCancellationReason] = useState("");
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [isBulkCancelling, setIsBulkCancelling] = useState(false);

  const doctorSchedules =
    user?.role === "doctor" ? getSchedulesByDoctor(user.id) : [];
  const doctorAppointments =
    user?.role === "doctor" ? getAppointmentsByDoctor(user.id) : [];

  const daySchedule = useMemo(() => {
    if (!user || user.role !== "doctor") return null;
    const dateStr = formatDate(selectedDate);

    const daySchedules = getActiveSchedulesForDate(
      doctorSchedules,
      user.id,
      selectedDate
    );
    if (daySchedules.length === 0) return null;

    const dayAppointments = doctorAppointments.filter(
      (a) => formatDate(a.date) === dateStr
    );

    const allSlots = daySchedules.flatMap((schedule) =>
      generateTimeSlots(
        schedule.start_time,
        schedule.end_time,
        schedule.slot_duration ?? schedule.slotDuration,
        schedule.appointment_type ?? schedule.appointmentType,
        dayAppointments,
        getScheduleConsultationMode(schedule)
      )
    );

    return {
      date: selectedDate,
      dayOfWeek: selectedDate.getDay(),
      slots: allSlots.sort((a, b) => a.startTime?.localeCompare(b.startTime)),
    };
  }, [selectedDate, doctorSchedules, doctorAppointments, user]);

  const futureAppointments = useMemo(() => {
    const todayStr = formatDate(new Date());

    return doctorAppointments
      .filter((apt) => {
        if (!isOccupiedAppointment(apt)) return false;
        return formatDate(apt.date) >= todayStr;
      })
      .sort((a, b) => {
        const dateA = formatDate(a.date);
        const dateB = formatDate(b.date);
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        return (a.start_time || "").localeCompare(b.start_time || "");
      });
  }, [doctorAppointments]);

  const upcomingAppointments = useMemo(() => {
    const nextWeekStr = formatDate(addDays(new Date(), 7));
    return futureAppointments.filter(
      (apt) => formatDate(apt.date) <= nextWeekStr
    );
  }, [futureAppointments]);

  const nextAppointments = futureAppointments.slice(0, 3);

  const todayAppointments = useMemo(() => {
    const today = formatDate(new Date());
    return doctorAppointments.filter(
      (apt) => formatDate(apt.date) === today && isOccupiedAppointment(apt)
    );
  }, [doctorAppointments]);

  const totalAppointments = doctorAppointments.filter(isOccupiedAppointment)
    .length;

  const bookedAppointmentsOnSelectedDates = useMemo(() => {
    if (!datesToCancel.length) return [];
    const dateStrings = datesToCancel.map((d) => formatDate(d));
    return doctorAppointments.filter(
      (apt) =>
        dateStrings.includes(formatDate(apt.date)) && isOccupiedAppointment(apt)
    );
  }, [datesToCancel, doctorAppointments]);

  const handleBulkCancel = async () => {
    if (!cancellationReason.trim()) {
      customToast(
        t("app.appointments.reasonRequired", "Debés ingresar un motivo de cancelación"),
        "danger"
      );
      return;
    }
    if (!datesToCancel.length) {
      customToast(
        t("app.appointments.selectDatesRequired", "Debés seleccionar al menos un día"),
        "danger"
      );
      return;
    }
    if (!bookedAppointmentsOnSelectedDates.length) {
      customToast(
        t("app.appointments.noAppointmentsToCancel", "No hay turnos tomados en los días seleccionados"),
        "danger"
      );
      return;
    }

    setIsBulkCancelling(true);
    try {
      const result = await cancelBulkByDates({
        doctorId: user.id,
        cancelledBy: user.id,
        dates: datesToCancel.map((d) => formatDate(d)),
        cancellationReason: cancellationReason.trim(),
        notifyPatients: true,
      });
      customToast(
        t("app.appointments.bulkCancelSuccess", "Turnos cancelados y pacientes notificados", {
          count: result.cancelled_count,
        }),
        "success"
      );
      setShowBulkConfirm(false);
      setCancelModeEnabled(false);
      setDatesToCancel([]);
      setCancellationReason("");
      await loadData();
    } catch (err) {
      customToast(
        err.message || t("app.appointments.cancelError", "Error al cancelar el turno"),
        "danger"
      );
    } finally {
      setIsBulkCancelling(false);
    }
  };

  const getSecretaryName = (secretaryId) => {
    const profile = secretaries.find((s) => s.id === secretaryId);
    if (profile) return utils.getStaffDisplayName(profile);
    const cached = getProfileFromCache(secretaryId);
    if (cached) return utils.getStaffDisplayName(cached);
    return t("app.appointments.secretaryFallback", "Secretaria");
  };

  const getAppointmentTypeLabel = (appointment) => {
    if (!appointment) return "";
    if (appointment.secretary_id) {
      return t("app.appointments.assignedBySecretary", "Asignado por {{name}}", {
        name: getSecretaryName(appointment.secretary_id),
      });
    }
    return (appointment.appointment_type || appointment.appointmentType) === "private"
      ? t("schedule.appointmentTypes.private", "Particular")
      : t("schedule.appointmentTypes.insurance", "Obra Social");
  };

  const getAppointmentTypeBadge = (appointment) => {
    if (!appointment) return "secondary";
    if (appointment.secretary_id || appointment.secretaryId) {
      return "outline";
    }
    return (appointment.appointment_type || appointment.appointmentType) ===
      "private"
      ? "appointmentPrivate"
      : "appointmentInsurance";
  };

  const getSlotTypeLabel = (slot) => {
    if (slot.appointment) {
      return getAppointmentTypeLabel(slot.appointment);
    }
    return (slot.appointmentType || slot.appointment_type) === "private"
      ? t("schedule.appointmentTypes.private", "Particular")
      : t("schedule.appointmentTypes.insurance", "Obra Social");
  };

  const getSlotTypeBadge = (slot) => {
    if (slot.appointment) {
      return getAppointmentTypeBadge(slot.appointment);
    }
    return slot.appointmentType === "private"
      ? "appointmentPrivate"
      : "appointmentInsurance";
  };

  const getPatientForAppointment = (appointment) => {
    if (!appointment) return null;
    const profile = appointment.patient_id
      ? getProfileFromCache(appointment.patient_id)
      : null;
    if (profile) return profile;
    const nameParts = (appointment.patient_name || "").trim().split(/\s+/);
    return {
      name: nameParts[0] || appointment.patient_name || t("app.appointments.patientFallback", "Paciente"),
      last_name: nameParts.slice(1).join(" "),
      patient_name: appointment.patient_name,
      emergency_contact_phone: appointment.patient_phone,
    };
  };

  const goToClinicalHistory = (appointment) => {
    // Turnos asignados a pacientes no registrados no tienen patient_id: no hay historia que ver
    if (!appointment.patient_id) {
      customToast(
        t(
          "app.appointments.unregisteredPatientHistory",
          "Este paciente no está registrado en el sistema. No se puede crear una historia clínica para pacientes no registrados."
        ),
        "warning"
      );
      return;
    }
    navigate(
      `/doctor/clinical-history/view?appointmentId=${appointment.id}&patientId=${appointment.patient_id}&doctorId=${appointment.doctor_id}&consultationDate=${formatDate(appointment.date)}`
    );
  };

  const CompactAppointmentCard = ({ appointment }) => {
    const patient = getPatientForAppointment(appointment);

    return (
      <div className="border rounded-lg p-3 space-y-2">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-500" />
            <span className="font-medium text-sm">
              {appointment.patient_name || t("app.appointments.patientFallback", "Paciente")}
            </span>
          </div>
          <Badge
            variant={getAppointmentTypeBadge(appointment)}
            className="text-xs"
          >
            {appointment.secretaryId || appointment.secretary_id
              ? t("app.appointments.hospital", "Hospital")
              : (appointment.appointment_type || appointment.appointmentType) === "private"
                ? t("schedule.appointmentTypes.private", "Particular")
                : t("schedule.appointmentTypes.insurance", "Obra Social")}
          </Badge>
        </div>

        <div className="space-y-1 text-sm">
          <div className="flex items-center gap-2">
            <span>{appointment.patient_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <CalendarDays className="w-3 h-3 text-gray-500" />
            <span>{formatDateDMY(appointment.date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3 text-gray-500" />
            <span>
              {formatClockTimeRange(appointment.start_time, appointment.end_time)}
            </span>
          </div>
          {appointment.patient_phone && (
            <div className="flex items-center gap-2">
              <Phone className="w-3 h-3 text-gray-500" />
              <span className="text-xs">{appointment.patient_phone}</span>
            </div>
          )}
          {appointment.consultation_mode && (
            <div>
              <ConsultationModeBadge mode={appointment.consultation_mode} />
            </div>
          )}
          {appointment.notes && (
            <div className="text-xs text-gray-600 p-2 bg-gray-50 rounded break-words">
              <span className="font-medium">
                {t("app.appointments.reason", "Motivo:")}
              </span>{" "}
              {appointment.notes}
            </div>
          )}

          {/* <a
              href={`/doctor/clinical-history/add?appointmentId=${
                appointment.id
              }&patientId=${appointment.patient_id}&doctorId=${
                appointment.doctor_id
              }&consultationDate=${formatDate(appointment.date)}`}
            >
              Agregar Historia
            </a> */}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => goToClinicalHistory(appointment)}
              className="flex items-end gap-2 text-sm text-gray-900 hover:underline"
            >
              <span>{t("app.clinical.viewHistory", "Ver Historia")}</span>
              <NotepadText className="h-4 w-4" />
            </button>
            <PatientSummaryInfoDialog patient={patient} />
          </div>
        </div>

        {appointment.price && (
          <div className="text-xs text-green-600 font-medium">
            ${appointment.price}
          </div>
        )}
      </div>
    );
  };

  if (!user || user.role !== "doctor") return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 mb-10">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => utils.goBack()}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />{t("common.backHome", "Volver al Inicio")}</Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t("app.appointments.myAppointments", "Mis Turnos")}</h1>
          <p className="text-gray-600">
            {t("app.appointments.doctorPageSubtitle", "Revisa todos tus turnos programados y disponibilidad")}
          </p>
        </div>

        {/* Resumen de estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="!p-4">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">
                    {upcomingAppointments.length}
                  </p>
                  <p className="text-sm text-gray-600">{t("app.appointments.next7Days", "Próximos 7 días")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="!p-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">
                    {todayAppointments.length}
                  </p>
                  <p className="text-sm text-gray-600">{t("app.appointments.today", "Hoy")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="!p-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold">{totalAppointments}</p>
                  <p className="text-sm text-gray-600">{t("app.appointments.totalSeen", "Total Atendidos")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="!p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="text-2xl font-bold">{doctorSchedules.length}</p>
                  <p className="text-sm text-gray-600">{t("app.appointments.schedulesConfigured", "Horarios Config.")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Box de Próximos Turnos */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t("app.appointments.upcoming", "Próximos Turnos")}</span>
              {futureAppointments.length > 3 && (
                <Badge variant="outline">
                  {t("app.appointments.scheduledCount", "{{count}} turnos programados", {
                    count: futureAppointments.length,
                  })}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {nextAppointments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">
                  {t("app.appointments.noUpcomingScheduled", "No tienes turnos programados")}
                </p>
                <Button asChild variant="outline">
                  <a href="/doctor/schedule">{t("app.scheduleConfig.configureLink", "Configurar Horarios")}</a>
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {nextAppointments.map((appointment) => (
                  <CompactAppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-20">
          {/* Calendario */}
          <Card className="min-w-[390px]">
            <CardHeader>
              <CardTitle>{t("app.appointments.selectDate", "Seleccionar Fecha")}</CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode={cancelModeEnabled ? "multiple" : "single"}
                selected={cancelModeEnabled ? datesToCancel : selectedDate}
                onSelect={(value) => {
                  if (cancelModeEnabled) {
                    setDatesToCancel(value || []);
                  } else if (value) {
                    setSelectedDate(value);
                  }
                }}
                className={`rounded-md border ${cancelModeEnabled ? "ring-2 ring-red-200" : ""}`}
              />
            </CardContent>
          </Card>

          {/* Horarios del día seleccionado */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                {getLocalizedDayName(selectedDate.getDay())}{" "}
                {formatDateDMY(selectedDate)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!daySchedule ? (
                <p className="text-gray-500 text-center py-8">
                  {t("app.appointments.noDaySchedule", "No tienes horarios configurados para este día")}
                </p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {daySchedule.slots.map((slot, index) => (
                    <div
                      key={index}
                      className={`flex justify-between items-center p-3 rounded-lg border ${
                        !slot.isAvailable
                          ? "bg-red-50 border-red-200 text-red-700"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">
                          {formatClockTimeRange(slot.startTime, slot.endTime)}
                        </span>
                        <Badge variant={getSlotTypeBadge(slot)}>
                          {getSlotTypeLabel(slot)}
                        </Badge>
                        <ConsultationModeBadge
                          mode={
                            slot.appointment?.consultation_mode ||
                            slot.consultationMode
                          }
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        {!slot.isAvailable ? (
                          <div className="text-right">
                            <div className="flex items-center gap-1 text-sm font-medium">
                              <User className="w-3 h-3" />
                              {slot.appointment.patient_name || t("app.appointments.patientFallback", "Paciente")}
                            </div>
                            {slot.appointment.patient_phone && (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <Phone className="w-3 h-3" />
                                {slot.appointment.patient_phone}
                              </div>
                            )}
                            {/* Solo mostrar precio si realmente lo tiene */}
                            {slot.appointment.price && (
                              <div className="text-xs text-green-600 font-medium">
                                ${slot.appointment.price}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Badge variant="outline">{t("app.appointments.available", "Disponible")}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cancelar día/s */}
        <Card className="mb-20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarX className="w-5 h-5 text-red-600" />
              {t("app.appointments.cancelDaysTitle", "Cancelar día/s")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="space-y-0.5">
                <Label htmlFor="enable-cancel-mode" className="text-base font-medium text-gray-900">
                  {t("app.appointments.enableCancellation", "Habilitar cancelación")}
                </Label>
                <p className="text-sm text-gray-500">
                  {cancelModeEnabled
                    ? t("app.appointments.cancellationEnabledHint", "Seleccioná los días en el calendario superior")
                    : t("app.appointments.cancellationDisabledHint", "Activá el switch para cancelar uno o más días")}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span
                  className={`text-sm font-medium ${
                    cancelModeEnabled ? "text-red-600" : "text-gray-500"
                  }`}
                >
                  {cancelModeEnabled
                    ? t("app.appointments.cancellationOn", "Activado")
                    : t("app.appointments.cancellationOff", "Desactivado")}
                </span>
                <Switch
                  id="enable-cancel-mode"
                  checked={cancelModeEnabled}
                  onCheckedChange={(next) => {
                    setCancelModeEnabled(next);
                    if (next) {
                      setDatesToCancel([selectedDate]);
                    } else {
                      setDatesToCancel([]);
                      setCancellationReason("");
                    }
                  }}
                />
              </div>
            </div>

            {cancelModeEnabled && (
              <>
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                  {t(
                    "app.appointments.selectDaysLegend",
                    "Elige los días a cancelar en el calendario de arriba"
                  )}
                </p>

                <div className="space-y-2">
                  <Label htmlFor="cancellation-reason">
                    {t("app.appointments.cancellationReason", "Motivo")}
                  </Label>
                  <Textarea
                    id="cancellation-reason"
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    placeholder={t(
                      "app.appointments.cancellationReasonPlaceholder",
                      "Ej: urgencia médica, vacaciones, etc."
                    )}
                    rows={3}
                  />
                </div>

                {datesToCancel.length > 0 && (
                  <p className="text-sm text-gray-600">
                    {t("app.appointments.selectedDaysCount", "{{count}} día(s) seleccionado(s) — {{appointments}} turno(s) afectado(s)", {
                      count: datesToCancel.length,
                      appointments: bookedAppointmentsOnSelectedDates.length,
                    })}
                  </p>
                )}

                <div className="space-y-1">
                  <Button
                    variant="destructive"
                    onClick={() => setShowBulkConfirm(true)}
                    disabled={isBulkCancelling}
                  >
                    {t("app.appointments.notifyPatients", "Avisar a mis pacientes")}
                  </Button>
                  <p className="text-xs text-gray-500">
                    {t(
                      "app.appointments.notifyPatientsHint",
                      "Se les notificará a tus pacientes vía email"
                    )}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

      </div>

      <Dialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
        <DialogContent className="w-[95vw] max-w-md rounded-xl p-6">
          <DialogTitle className="text-lg font-bold text-gray-900 mb-4">
            {t(
              "app.appointments.confirmCancelGeneric",
              "¿Estás seguro que deseas cancelar?"
            )}
          </DialogTitle>
          <p className="text-gray-600 text-sm mb-2">
            {t("app.appointments.bulkCancelConfirmDesc", "Se cancelarán {{count}} turno(s) y se enviará un email a cada paciente afectado.", {
              count: bookedAppointmentsOnSelectedDates.length,
            })}
          </p>
          {cancellationReason.trim() && (
            <p className="text-sm text-gray-500 mb-4">
              <span className="font-medium">{t("app.appointments.cancellationReason", "Motivo")}:</span>{" "}
              {cancellationReason.trim()}
            </p>
          )}
          <div className="flex gap-3 justify-end">
            <DialogClose asChild>
              <Button variant="outline" disabled={isBulkCancelling}>
                {t("common.cancel", "Cancelar")}
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleBulkCancel}
              disabled={isBulkCancelling}
            >
              {isBulkCancelling
                ? "..."
                : t("app.appointments.confirmYes", "Sí, cancelar")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
